import React, { useCallback, useMemo, useState } from 'react';

import { useHistory } from 'react-router-dom';

import { AutoAwesome as AiIcon, Visibility as ViewIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { useGetAttractionCities, useListAttractions, useUpdateAttractionActive } from '~/hooks/apis/attractions';

import {
  ATTRACTION_TYPES,
  type Attraction,
  type AttractionType,
  type ListAttractionsParams,
  formatAttractionType,
} from '~/types/attractionTypes';

const DEFAULT_PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: 'popularity', label: 'Popularity' },
  { value: 'most_offers', label: 'Most Offers' },
  { value: 'name', label: 'Name' },
] as const;

export default function AttractionsPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;
  const history = useHistory();

  const activeMutation = useUpdateAttractionActive();

  // Pagination state
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Filter inputs (not yet applied)
  const [countryInput, setCountryInput] = useState('');
  const [cityInput, setCityInput] = useState('');
  const [typeInput, setTypeInput] = useState<AttractionType | ''>('');
  const [sortInput, setSortInput] = useState<string>('popularity');

  // Applied filters (triggers query)
  const [filters, setFilters] = useState<ListAttractionsParams>({
    sort: 'popularity',
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
  });

  // Fetch cities for the filter dropdowns
  const { data: citiesData } = useGetAttractionCities();
  const cities = citiesData?.result ?? [];

  // Derive unique countries from cities data
  const countries = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cities) {
      if (c.country && c.countryCode) map.set(c.country, c.countryCode);
    }
    return Array.from(map.entries())
      .map(([country, countryCode]) => ({ country, countryCode }))
      .sort((a, b) => a.country.localeCompare(b.country));
  }, [cities]);

  // Filter cities by selected country
  const filteredCities = useMemo(
    () => (countryInput ? cities.filter((c) => c.country === countryInput) : cities),
    [cities, countryInput],
  );

  // AI-Powered Analytics
  const analytics = useMemo(() => {
    if (cities.length === 0) return null;
    const totalAttractions = cities.reduce((sum, c) => sum + c.attractionCount, 0);
    const totalCovered = cities.reduce((sum, c) => sum + c.coveredCount, 0);
    const coverageRate = totalAttractions > 0 ? Math.round((totalCovered / totalAttractions) * 100) : 0;
    const citiesWithCoverage = cities.filter((c) => c.coveredCount > 0).length;
    const topCity = [...cities].sort((a, b) => b.attractionCount - a.attractionCount)[0];
    return { totalAttractions, totalCovered, coverageRate, citiesWithCoverage, totalCities: cities.length, topCity };
  }, [cities]);

  // Build query params from pagination + filters
  const queryParams: ListAttractionsParams = useMemo(
    () => ({
      ...filters,
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize,
    }),
    [paginationModel, filters],
  );

  const { data, isLoading, isFetching, error } = useListAttractions(queryParams);

  const attractions: Attraction[] = data?.result ?? [];
  const totalCount = data?.total ?? 0;

  /** Apply search filters and reset to first page */
  const handleSearch = useCallback(() => {
    setFilters({
      country: countryInput || undefined,
      city: cityInput || undefined,
      type: typeInput || undefined,
      sort: sortInput || undefined,
      limit: paginationModel.pageSize,
      offset: 0,
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [countryInput, cityInput, typeInput, sortInput, paginationModel.pageSize]);

  /** Clear all filters */
  const handleClearFilters = useCallback(() => {
    setCountryInput('');
    setCityInput('');
    setTypeInput('');
    setSortInput('popularity');
    setFilters({ sort: 'popularity', limit: DEFAULT_PAGE_SIZE, offset: 0 });
    setPaginationModel({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  }, []);

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  const handleToggleActive = useCallback(
    (attractionId: string, currentActive: boolean) => {
      activeMutation.mutate({ attractionId, active: !currentActive });
    },
    [activeMutation],
  );

  const columns: Array<GridColDef<Attraction>> = useMemo(
    () => [
      {
        field: 'actions',
        headerName: 'View',
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }: GridRenderCellParams<Attraction>) => (
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                history.push(`/attractions/${row.attractionId}`);
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        field: 'imageUrl',
        headerName: 'Image',
        width: 70,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ value }: GridRenderCellParams<Attraction>) =>
          value ? (
            <Box
              component="img"
              src={`${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_100,h_100/${
                value as string
              }`}
              alt=""
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement?.setAttribute('data-fallback', 'true');
              }}
              sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
            />
          ) : (
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 1,
                bgcolor: 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                N/A
              </Typography>
            </Box>
          ),
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }: GridRenderCellParams<Attraction>) => (
          <Box sx={{ overflow: 'hidden', py: 0.5 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
              {row.attractionId}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'city',
        headerName: 'City / Country',
        flex: 0.8,
        minWidth: 160,
        renderCell: ({ row }: GridRenderCellParams<Attraction>) => (
          <Typography variant="body2">
            {row.city}, {row.country}
          </Typography>
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 140,
        renderCell: ({ value }: GridRenderCellParams<Attraction>) => (
          <Chip label={formatAttractionType(value as string)} size="small" />
        ),
      },
      {
        field: 'offerCount',
        headerName: 'Offers',
        width: 80,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'popularityRank',
        headerName: 'Popularity',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }: GridRenderCellParams<Attraction>) => (
          <Typography variant="body2">#{value as number}</Typography>
        ),
      },
      {
        field: 'active',
        headerName: 'Active',
        width: 80,
        renderCell: ({ row }: GridRenderCellParams<Attraction>) => (
          <Switch
            checked={row.active}
            size="small"
            onChange={(event) => {
              event.stopPropagation();
              handleToggleActive(row.attractionId, row.active);
            }}
            onClick={(event) => event.stopPropagation()}
          />
        ),
      },
    ],
    [handleToggleActive, history],
  );

  return (
    <Box>
      {/* AI-Powered Analytics */}
      {analytics && (
        <Paper elevation={1} sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <AiIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="subtitle2" color="primary.main" fontWeight="bold">
              AI-Powered Analytics
            </Typography>
          </Stack>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <Tooltip title="Total points of interest generated by the AI curation pipeline" arrow>
              <Box sx={{ minWidth: 120, textAlign: 'center', cursor: 'help' }}>
                <Typography variant="h5" fontWeight="bold">
                  {analytics.totalAttractions.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Attractions
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip
              title="Percentage of attractions with at least one offer linked via proximity match (5km radius)"
              arrow
            >
              <Box sx={{ minWidth: 120, textAlign: 'center', cursor: 'help' }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  color={analytics.coverageRate > 70 ? 'success.main' : 'warning.main'}
                >
                  {analytics.coverageRate}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Offer Coverage
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Cities with at least one attraction linked to an offer, out of total cities" arrow>
              <Box sx={{ minWidth: 120, textAlign: 'center', cursor: 'help' }}>
                <Typography variant="h5" fontWeight="bold">
                  {analytics.citiesWithCoverage}/{analytics.totalCities}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Cities Covered
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Attractions that have at least one experience offer matched by proximity" arrow>
              <Box sx={{ minWidth: 120, textAlign: 'center', cursor: 'help' }}>
                <Typography variant="h5" fontWeight="bold">
                  {analytics.totalCovered.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Linked Offers
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Number of distinct countries with AI-generated attractions" arrow>
              <Box sx={{ minWidth: 120, textAlign: 'center', cursor: 'help' }}>
                <Typography variant="h5" fontWeight="bold">
                  {countries.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Countries
                </Typography>
              </Box>
            </Tooltip>
          </Stack>
        </Paper>
      )}

      {/* Filters */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.country}
            value={countries.find((c) => c.country === countryInput) ?? null}
            onChange={(_, value) => {
              setCountryInput(value?.country ?? '');
              setCityInput('');
            }}
            renderInput={(params) => <TextField {...params} label="Country" />}
            sx={{ minWidth: 200 }}
            size="small"
          />
          <Autocomplete
            options={filteredCities}
            getOptionLabel={(option) => `${option.city} (${option.attractionCount})`}
            value={filteredCities.find((c) => c.city === cityInput) ?? null}
            onChange={(_, value) => setCityInput(value?.city ?? '')}
            renderInput={(params) => <TextField {...params} label="City" />}
            sx={{ minWidth: 220 }}
            size="small"
          />
          <Autocomplete
            options={[...ATTRACTION_TYPES]}
            getOptionLabel={(option) => formatAttractionType(option)}
            value={(typeInput as AttractionType) || null}
            onChange={(_, value) => setTypeInput((value as AttractionType) ?? '')}
            renderInput={(params) => <TextField {...params} label="Type" />}
            sx={{ minWidth: 180 }}
            size="small"
          />
          <TextField
            select
            label="Sort"
            value={sortInput}
            onChange={(event) => {
              const newSort = event.target.value;
              setSortInput(newSort);
              setFilters((prev) => ({ ...prev, sort: newSort || undefined, offset: 0 }));
              setPaginationModel((prev) => ({ ...prev, page: 0 }));
            }}
            sx={{ minWidth: 140 }}
            size="small"
          >
            {SORT_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="contained" size="small" onClick={handleSearch}>
              Search
            </Button>
            <Button variant="text" size="small" onClick={handleClearFilters}>
              Clear
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Error alert */}
      {error && (
        <Box mb={3}>
          <Alert severity="error">
            Error loading attractions: {error instanceof Error ? error.message : String(error)}
          </Alert>
        </Box>
      )}

      {/* Attractions data grid */}
      <Paper elevation={0} sx={{ height: 650 }}>
        <DataGridPro
          rows={attractions}
          columns={columns}
          loading={isLoading || isFetching}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50]}
          rowCount={totalCount}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          rowHeight={60}
          sx={{
            height: '100%',
            '& .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-row:hover': {
              bgcolor: alpha(themeColor, 0.08),
            },
          }}
        />
      </Paper>
    </Box>
  );
}
